// In: src/components/FindPubs.tsx

import React from 'react';
import {
    Box,
    Button,
    Paper,
    Snackbar,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Dispatch, SetStateAction, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getNearbyPubs, getPubRaycasting, checkSolarCoverage } from "../API"; // Added checkSolarCoverage import
import MapView from "./MapView";
import { themeColors } from '../styles/theme';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import AdSense from './AdSense';
import Loader from "./Loader";
import ScrollIndicator from './ScrollIndicator';
import {
    trackAreaSearch,
    trackSearchResultsMetrics,
    trackFiltersApplied,
    trackSearchToViewConversion,
    trackViewPreference,
    trackPubsViewedInSearch
} from "../utils/analytics";
import { getCountryFromLocale } from "../utils/getCountryFromLocale";
import { calculateBeerMugRating, getCurrentSunIntensity } from "../utils/ratingCalculations";
import { FiltersAndTabSelector } from "./FiltersAndTabSelector";
import TopNav from "./TopNav";
import { Footer } from './Footer';
import PubCard from './PubCard';
import { useLocation } from '../contexts/LocationContext';
import { Helmet } from 'react-helmet';
import { isCrawler } from '../utils/isCrawler';

export interface HeatmapData {
    timestamps: string[];
    values: number[];
}

export interface PubObject {
    name: string;
    distance: number;
    address?: string;
    city?: string;
    rating?: number;
    google_place_id: string;
    garden_drawn?: boolean;
    garden_moderated?: boolean;
    has_no_garden?: boolean;
    gardens?: Array<{
        name: string;
        uid: string;
        heatmap?: HeatmapData;
        is_uncertain?: boolean;
        garden_size?: number;
    }>;
    heatmap?: HeatmapData;
    latitude?: number;
    longitude?: number;
    sunrise?: string;
    sunset?: string;
    coverage_quality?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
    currentSunIntensity?: number;
}

interface FindPubsProps {
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    setShowBurgerMenuIcon: Dispatch<SetStateAction<boolean>>;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
}

interface GardenDataForAPI {
    heatmap_1d: HeatmapData;
    total_sun_points: number;
}

interface PubRaycastingResponse {
    [key: string]: GardenDataForAPI | string | unknown;
    pub_id: string;
    date: string;
    original_query_date: string;
    status?: string;
    has_no_garden?: boolean;
    garden_details?: Record<string, { name: string; is_uncertain: boolean; garden_size?: number }>;
    sunrise?: string;
    sunset?: string;
    coverage_quality?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
}

// Central place to define how many pubs we want.
const DESIRED_PUB_COUNT = 20;

const FindPubs = ({
    search,
    setShowBurgerMenuIcon,
    loading: propLoading,
    setLoading: propSetLoading
}: FindPubsProps) => {
    const { 
        searchLocation,
        isCustomSearchMode,
        requestUserLocation,
        isLoadingUserLocation 
    } = useLocation();

    // Detect if this is a bot to pass to components
    const isBot = useMemo(() => {
        // Debug parameter to force bot mode
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('debug') === 'bot') {
            return true;
        }
        return isCrawler();
    }, []);
    
    const [pubs, setPubs] = useState<PubObject[]>([]);
    const [tab, setTab] = useState<'list' | 'map'>(() => {
        const saved = localStorage.getItem('findPubsFilters');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.tab || 'list';
            } catch {
                return 'list';
            }
        }
        return 'list';
    });
    const navigate = useNavigate();
    const [raycastLoading, setRaycastLoading] = useState<boolean>(false);
    const [raycastingPubIds, setRaycastingPubIds] = useState<Set<string>>(new Set());

    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [geoError, setGeoError] = useState<string | null>(null);
    const [coverageError, setCoverageError] = useState<string | null>(null); // Add coverage error state

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const timelineMode = isMobile ? 'compact' : 'full';

    const latitude = searchLocation?.latitude ?? 51.4700777;
    const longitude = searchLocation?.longitude ?? -0.0245202;
    const isLoading = propLoading || isLoadingUserLocation;

    const hasInitiallyFetched = useRef<boolean>(false);
    const fetchCount = useRef(0);

    const [sortBy, setSortBy] = useState<'distance' | 'rating'>(() => {
        const saved = localStorage.getItem('findPubsFilters');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.sortBy || 'distance';
            } catch {
                return 'distance';
            }
        }
        return 'distance';
    });
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
        const saved = localStorage.getItem('findPubsFilters');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.sortDirection || 'asc';
            } catch {
                return 'asc';
            }
        }
        return 'asc';
    });
    const [hideNoGardens, setHideNoGardens] = useState<boolean>(() => {
        const saved = localStorage.getItem('findPubsFilters');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.hideNoGardens || false;
            } catch {
                return false;
            }
        }
        return false;
    });
    const [searchRadius, setSearchRadius] = useState<number>(() => {
        const savedParams = localStorage.getItem('searchAreaParams');
        if (savedParams) { try { return JSON.parse(savedParams).radius || 1000; } catch { /* ignore */ } }
        return 1000;
    });
    const [customRadiusInput, setCustomRadiusInput] = useState<number>(searchRadius);
    const [activeFilters, setActiveFilters] = useState<number>(0);
    const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<null | HTMLElement>(null);

    // Track which pubs have been viewed
    const viewedPubsRef = useRef<Set<string>>(new Set());
    const observerRef = useRef<IntersectionObserver | null>(null);

    useEffect(() => { window.scrollTo(0, 0); }, []);
    useEffect(() => { return () => { if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current); }; }, []);

    const handleEditSearchArea = () => { 
        navigate('/search-area'); 
    };

    useEffect(() => { setShowBurgerMenuIcon(false); }, [setShowBurgerMenuIcon]);

    const updatePubInArray = (pubsArray: PubObject[], pubId: string, updatedData: Partial<PubObject>): PubObject[] => {
        const index = pubsArray.findIndex(p => p.google_place_id === pubId);
        if (index === -1) return pubsArray;
        const newArray = [...pubsArray];
        newArray[index] = { ...newArray[index], ...updatedData };
        return newArray;
    };

    // Function to calculate current sun intensity from heatmap data
    const getCurrentSunIntensity = (heatmap: HeatmapData | undefined): number => {
        if (!heatmap || !heatmap.timestamps || !Array.isArray(heatmap.timestamps) || !heatmap.values || !Array.isArray(heatmap.values)) {
            return 0;
        }
        
        if (heatmap.timestamps.length === 0 || heatmap.values.length === 0) {
            return 0;
        }
        
        const now = new Date();
        const timestamps = heatmap.timestamps;
        const values = heatmap.values;
        
        // Get current time in minutes from midnight (ignoring date)
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        
        // Find the nearest timestamp by comparing minutes from midnight only (ignoring date)
        let nearestIndex = 0;
        let minMinutesDiff = Infinity;
        
        timestamps.forEach((timestamp, index) => {
            try {
                const timestampDate = new Date(timestamp);
                
                // Get this time in minutes from midnight
                const timeMinutes = timestampDate.getHours() * 60 + timestampDate.getMinutes();
                
                // Calculate difference, handling the wrap around midnight case
                let minutesDiff = Math.abs(timeMinutes - currentMinutes);
                // Consider times that wrap around midnight (e.g. 23:50 vs 00:10)
                minutesDiff = Math.min(minutesDiff, 1440 - minutesDiff);
                
                if (minutesDiff < minMinutesDiff) {
                    minMinutesDiff = minutesDiff;
                    nearestIndex = index;
                }
            } catch (e) {
                // Just skip problematic timestamps
            }
        });
        
        // Make sure the index is in bounds
        if (nearestIndex >= values.length) {
            return 0;
        }
        
        // Get the current value (0-1 percentage)
        const currentValue = typeof values[nearestIndex] === 'number' ? values[nearestIndex] : 0;
        
        // Ensure the value is within bounds
        return Math.max(0, Math.min(1, currentValue));
    };
    
    //- MODIFIED: This function is now simpler as it doesn't need to fetch place details.
    const fetchPubRaycasting = useCallback(async (pub: PubObject) => {
        try {
            const response = await getPubRaycasting(pub.google_place_id, false, false);
            const pubApiData = response.data as PubRaycastingResponse;
            const dataToUpdate: Partial<PubObject> = {
                has_no_garden: pubApiData.has_no_garden || pubApiData.status === 'no_garden',
                sunrise: pubApiData.sunrise,
                sunset: pubApiData.sunset,
                coverage_quality: pubApiData.coverage_quality,
            };

            if (!dataToUpdate.has_no_garden && pubApiData.garden_details) {
                const gardenDetails = pubApiData.garden_details;
                const gardenDetailKeys = Object.keys(gardenDetails);
                const heatmapKeys = Object.keys(pubApiData).filter(key =>
                    typeof pubApiData[key] === 'object' && pubApiData[key] !== null && (pubApiData[key] as any).heatmap_1d &&
                    !['pub_id', 'date', 'original_query_date', 'status', 'garden_details', 'has_no_garden', 'mesh_uris', 'centre_ecef', 'copyrights', 'sun_vector', 'sunrise', 'sunset', 'session_id'].includes(key)
                );
                const allGardenIds = Array.from(new Set([...gardenDetailKeys, ...heatmapKeys]));
                const processedGardens = allGardenIds
                    .filter(id => pubApiData[id] && (pubApiData[id] as any).heatmap_1d)
                    .map(subAreaId => {
                        const gardenApiData = pubApiData[subAreaId] as GardenDataForAPI;
                        return {
                            name: gardenDetails[subAreaId]?.name || `Garden ${subAreaId.substring(0,8)}`,
                            uid: subAreaId,
                            heatmap: gardenApiData.heatmap_1d,
                            is_uncertain: gardenDetails[subAreaId]?.is_uncertain || false,
                            garden_size: gardenDetails[subAreaId]?.garden_size,
                        };
                    });
                dataToUpdate.gardens = processedGardens;
                let totalSunPoints = 0;
                let gardensWithSunData = 0;
                processedGardens.forEach(g => {
                    const gardenApiData = pubApiData[g.uid] as GardenDataForAPI;
                    if (gardenApiData?.total_sun_points) { totalSunPoints += gardenApiData.total_sun_points; gardensWithSunData++; }
                });
                if (gardensWithSunData > 0) dataToUpdate.rating = calculateBeerMugRating(totalSunPoints / gardensWithSunData);
                if (processedGardens.length > 0 && processedGardens[0].heatmap) dataToUpdate.heatmap = processedGardens[0].heatmap;
                else if (pubApiData.heatmap_1d && typeof pubApiData.heatmap_1d !== 'string') dataToUpdate.heatmap = pubApiData.heatmap_1d as HeatmapData;
            }
            setPubs(prev => updatePubInArray(prev, pub.google_place_id, dataToUpdate));
        } catch (error) {
            console.error(`Error fetching raycasting data for pub ${pub.google_place_id}:`, error);
            setPubs(prev => updatePubInArray(prev, pub.google_place_id, { heatmap: undefined }));
        } finally {
            setRaycastingPubIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(pub.google_place_id);
                if (newSet.size === 0) {
                    setRaycastLoading(false);
                }
                return newSet;
            });
        }
    }, []);

    //- MODIFIED: Main data fetching useEffect with coverage check
    useEffect(() => {
        if (search) return;
        if (!searchLocation) {
            propSetLoading(false);
            return;
        }
        
        propSetLoading(true);
        hasInitiallyFetched.current = false;
        setCoverageError(null); // Clear any previous coverage errors
        
        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = setTimeout(() => {
            propSetLoading(false); 
            setRaycastLoading(false);
            setGeoError("Loading timed out. Please refresh.");
        }, 30000);

        const fetchPubsData = async () => {
            try {
                // 1. FIRST: Check solar coverage for the search area
                try {
                    const coverageResponse = await checkSolarCoverage(latitude, longitude);
                    
                    if (!coverageResponse.has_coverage || coverageResponse.status === 'no_coverage') {
                        // Area is not covered by Solar API
                        setCoverageError(
                            "We currently support pubs in the UK, parts of Europe, and the US. Please try a different location."
                        );
                        propSetLoading(false);
                        hasInitiallyFetched.current = true;
                        return; // Don't proceed with pub search
                    }
                    
                    // Coverage check passed, proceed with normal flow
                } catch (coverageCheckError) {
                    console.error("Coverage check failed:", coverageCheckError);
                    // If coverage check fails, we'll still proceed but log the error
                    // This ensures the app doesn't break if the coverage API is down
                }

                // 2. THEN: Proceed with normal pub fetching if coverage is available
                const country = getCountryFromLocale();
                // Pass DESIRED_PUB_COUNT to the API call
                const response = await getNearbyPubs(latitude, longitude, searchRadius, country, true, DESIRED_PUB_COUNT);
                const fetchedPubs: PubObject[] = response.data.pubs || [];
                
                setPubs(fetchedPubs);
                hasInitiallyFetched.current = true;
                trackSearchResultsMetrics(fetchedPubs.length, fetchedPubs.filter(p => p.garden_drawn).length, latitude, longitude, searchRadius);
                propSetLoading(false);

                const pubsToQuery = fetchedPubs.filter(p => p.has_no_garden !== true);
                if (pubsToQuery.length > 0) {
                    setRaycastingPubIds(new Set(pubsToQuery.map(p => p.google_place_id)));
                    setRaycastLoading(true);
                    
                    // The backend now handles enrichment, so we just fetch raycasting data for all.
                    Promise.allSettled(pubsToQuery.map(p => fetchPubRaycasting(p)))
                        .catch(error => {
                            console.error("Error processing batch of pub raycasting calls:", error);
                        });
                } else {
                    setRaycastLoading(false);
                    setRaycastingPubIds(new Set());
                }

            } catch (error: any) {
                console.error("Error fetching nearby pubs:", error);
                setGeoError("Error loading pubs. Please refresh.");
                hasInitiallyFetched.current = true;
                propSetLoading(false);
            } finally {
                if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
            }
        };
        
        // Track the search and start fetching
        trackAreaSearch(latitude, longitude, searchRadius);
        fetchPubsData();
        
        return () => { if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current); };
    }, [search, latitude, longitude, searchRadius, searchLocation, propSetLoading, fetchPubRaycasting, fetchCount.current]);
    
    // ... (rest of the FindPubs component is unchanged)

    const sortAndFilterPubs = useCallback((pubsToFilter: PubObject[], currentSearchTerm: string, currentHideNoGardens: boolean, currentSortBy: string, currentSortDirection: string) => {
        let filtered = [...pubsToFilter];
        if (currentSearchTerm) filtered = filtered.filter(pub => pub.name.toLowerCase().includes(currentSearchTerm.toLowerCase()));
        if (currentHideNoGardens) filtered = filtered.filter(pub => !pub.has_no_garden && (pub.garden_drawn || (pub.gardens && pub.gardens.length > 0)));
        return filtered.sort((a, b) => {
            let comparison = 0;
            if (currentSortBy === 'distance') {
                comparison = a.distance - b.distance;
            } else {
                // Sort by current sun intensity instead of overall rating
                const currentSunA = getCurrentSunIntensity(a.heatmap) || (a.gardens && a.gardens.length > 0 ? Math.max(...a.gardens.map(g => getCurrentSunIntensity(g.heatmap))) : 0);
                const currentSunB = getCurrentSunIntensity(b.heatmap) || (b.gardens && b.gardens.length > 0 ? Math.max(...b.gardens.map(g => getCurrentSunIntensity(g.heatmap))) : 0);
                comparison = currentSunB === currentSunA ? a.distance - b.distance : currentSunB - currentSunA;
            }
            return currentSortDirection === 'asc' ? comparison : -comparison;
        });
    }, [getCurrentSunIntensity]);

    const processedPubs = useMemo(() => {
        return sortAndFilterPubs(pubs, search, hideNoGardens, sortBy, sortDirection);
    }, [pubs, search, hideNoGardens, sortBy, sortDirection, sortAndFilterPubs]);

    // Set up intersection observer for tracking viewed pubs
    useEffect(() => {
        // Only track for list view, not map view
        if (tab !== 'list' || processedPubs.length === 0) return;

        // Create observer to track when pubs come into view
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const pubId = entry.target.getAttribute('data-pub-id');
                        if (pubId && !viewedPubsRef.current.has(pubId)) {
                            viewedPubsRef.current.add(pubId);
                        }
                    }
                });
            },
            {
                threshold: 0.1 // Trigger when 10% of the card is visible
            }
        );

        // Observe all pub cards
        const pubCards = document.querySelectorAll('[data-pub-id]');
        pubCards.forEach(card => observerRef.current?.observe(card));

        // Cleanup
        return () => {
            observerRef.current?.disconnect();
        };
    }, [tab, processedPubs]);

    // Send analytics when user leaves the page or changes view
    useEffect(() => {
        return () => {
            if (viewedPubsRef.current.size > 0 && processedPubs.length > 0) {
                trackPubsViewedInSearch(viewedPubsRef.current.size, processedPubs.length);
            }
        };
    }, [processedPubs.length]);

    // Also track when switching tabs or navigating away
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (viewedPubsRef.current.size > 0 && processedPubs.length > 0) {
                trackPubsViewedInSearch(viewedPubsRef.current.size, processedPubs.length);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [processedPubs.length]);

    useEffect(() => {
        let count = 0;
        if (hideNoGardens) count++; if (searchRadius !== 1000) count++;
        if (sortBy !== 'distance') count++; if (sortDirection !== 'asc') count++;
        setActiveFilters(count);
    }, [hideNoGardens, searchRadius, sortBy, sortDirection]);

    const handleFilterMenuClose = () => setFilterMenuAnchorEl(null);

    const handleApplyFilters = () => {
        setSearchRadius(customRadiusInput);
        handleFilterMenuClose();
        trackFiltersApplied({ sortBy, sortDirection, hideNoGardens, customRadius: customRadiusInput });
        fetchCount.current += 1; 
        hasInitiallyFetched.current = false; // Force re-fetch with Lottie
    };

    const handleViewPub = useCallback((pubObjectToView: PubObject) => {
        trackSearchToViewConversion(pubObjectToView.google_place_id, (pubObjectToView.garden_drawn === true) || ((pubObjectToView.gardens?.length ?? 0) > 0), sortBy, { sortDirection, hideNoGardens, searchRadius });
        const completeData: PubObject = { ...pubObjectToView, name: pubObjectToView.name || 'Unknown Pub', google_place_id: pubObjectToView.google_place_id, latitude: pubObjectToView.latitude ?? 0, longitude: pubObjectToView.longitude ?? 0, address: pubObjectToView.address || '', garden_drawn: pubObjectToView.garden_drawn || false, garden_moderated: pubObjectToView.garden_moderated || false, has_no_garden: pubObjectToView.has_no_garden || false, distance: pubObjectToView.distance || 0, };
        
        // --- OPTIMIZATION: Pass the entire list of nearby pubs to avoid redundant API calls ---
        navigate(`/pub/${pubObjectToView.google_place_id}`, { 
            state: { 
                pubToView: completeData,
                nearbyPubs: processedPubs.filter(p => p.google_place_id !== pubObjectToView.google_place_id) // Exclude the selected pub from nearby list
            } 
        });
    }, [navigate, sortBy, sortDirection, hideNoGardens, searchRadius, processedPubs]);

    const handleSubmitGarden = useCallback((pubForSubmission: PubObject) => {
        const completeData: PubObject = { ...pubForSubmission, name: pubForSubmission.name || 'Unknown Pub', google_place_id: pubForSubmission.google_place_id, latitude: pubForSubmission.latitude ?? 0, longitude: pubForSubmission.longitude ?? 0, address: pubForSubmission.address || '', garden_drawn: pubForSubmission.garden_drawn || false, garden_moderated: pubForSubmission.garden_moderated || false, has_no_garden: pubForSubmission.has_no_garden || false, distance: pubForSubmission.distance || 0, };
        navigate(`/submit?pub_id=${pubForSubmission.google_place_id}`, { state: { selectedPub: completeData }});
    }, [navigate]);

    useEffect(() => { if (pubs.length > 0) trackViewPreference(tab, pubs.length); }, [tab, pubs.length]);

    // Save filter preferences to localStorage
    useEffect(() => {
        const filtersToSave = {
            sortBy,
            sortDirection,
            hideNoGardens,
            tab
        };
        localStorage.setItem('findPubsFilters', JSON.stringify(filtersToSave));
    }, [sortBy, sortDirection, hideNoGardens, tab]);

    return (
        <Box sx={{ width: '100%', minHeight: '100vh' }}>
            <Helmet>
                <title>{isLoading && !hasInitiallyFetched.current ? 'Finding pubs in your area...' : 'Pubs Near Me | Suntap'}</title>
                <meta name="description" content="Find the sunniest pub gardens near you with real-time sun tracking. Filter by distance, sun rating, and garden availability to discover your perfect sunny spot." />
                <link rel="canonical" href="https://sun-tap.com/pubs" />
                <meta name="keywords" content="pubs near me, sunny pub gardens, beer gardens nearby, pub finder, outdoor pubs, sunny pints, suntap search" />
                <meta property="og:title" content="Pubs Near Me | Find Sunny Beer Gardens" />
                <meta property="og:description" content="Find the sunniest pub gardens near you with real-time sun tracking. Filter by distance, sun rating, and garden availability." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://sun-tap.com/pubs" />
                <meta property="og:image" content="https://sun-tap.com/banner-1200-630.png" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Pubs Near Me | Find Sunny Beer Gardens" />
                <meta name="twitter:description" content="Find the sunniest pub gardens near you with real-time sun tracking." />
                <meta name="twitter:image" content="https://sun-tap.com/banner-1200-630.png" />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <TopNav title={'Find pubs'} />
            <Box sx={{ display: 'flex', justifyContent: 'center', px: '5%' }}>
                <Box sx={{ width: '100%', maxWidth: 800 }}>
                    <FiltersAndTabSelector
                        tab={tab}
                        setTab={setTab}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        sortDirection={sortDirection}
                        setSortDirection={setSortDirection}
                        customRadius={customRadiusInput}
                        setCustomRadius={setCustomRadiusInput}
                        handleApplyFilters={handleApplyFilters}
                        hideNoGardens={hideNoGardens}
                        setHideNoGardens={setHideNoGardens}
                        loading={isLoading && !hasInitiallyFetched.current} // Pass Lottie loading state to filters
                    />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2, px: '5%', pb: 4 }}>
                {isCustomSearchMode && (
                    <Box sx={{ width: '100%', maxWidth: '1400px', display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditLocationIcon />}
                            onClick={handleEditSearchArea}
                            sx={{
                                background: 'transparent',
                                color: 'black',
                                fontSize: '1.1rem',
                                fontFamily: "ApfelGrotezkRegular",
                                borderRadius: '48px',
                                transition: 'all 0.2s ease',
                                border: `2px solid black`,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    borderColor: 'black',
                                }
                            }}
                        >
                            Change search area
                        </Button>
                    </Box>
                )}
                {geoError && !isCustomSearchMode && (
                    <Snackbar open={true} message={geoError} action={ <Button color="inherit" size="small" onClick={() => requestUserLocation()} startIcon={<MyLocationIcon />} sx={{ ':hover': { border: '1px solid white' }, border: '1px solid transparent' }}> Grant Permission </Button> }/>
                )}
                {/* Coverage Error Message */}
                {coverageError && (
                    <Paper elevation={2} sx={{ 
                        textAlign: 'center', 
                        p: 3, 
                        borderRadius: '24px', 
                        background: themeColors.red, 
                        backdropFilter: 'blur(16px)', 
                        width: '100%', 
                        mt: 2 
                    }}>
                        <Typography variant="h5" sx={{ 
                            color: themeColors.darkBackground, 
                            fontFamily: "ApfelGrotezkRegular",
                            mb: 2
                        }}>
                            We're sorry, but this area isn't supported yet.
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            color: 'rgba(34, 34, 34, 0.8)', 
                            fontFamily: "ApfelGrotezkRegular",
                            mb: 2
                        }}>
                            {coverageError}
                        </Typography>
                        {isCustomSearchMode && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditLocationIcon />}
                                onClick={handleEditSearchArea}
                                sx={{
                                    background: 'transparent',
                                    color: 'black',
                                    fontSize: '1rem',
                                    fontFamily: "ApfelGrotezkRegular",
                                    borderRadius: '48px',
                                    transition: 'all 0.2s ease',
                                    border: `2px solid black`,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                        borderColor: 'black',
                                    }
                                }}
                            >
                                Please try a different area.
                            </Button>
                        )}
                    </Paper>
                )}
                {/* Lottie Loader: Shows only during initial pub list fetch OR geoLoading */}
                {(isLoading && !hasInitiallyFetched.current) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh', width: '100%' }}><Loader /></Box>
                )}

                {/* Content Area: Shows after initial fetch (Lottie is off) */}
                {/* "No Pubs Found" Message */}
                {!propLoading && hasInitiallyFetched.current && processedPubs.length === 0 && !search && !coverageError && (
                   <Paper elevation={2} sx={{ textAlign: 'center', p: 3, borderRadius: '24px', background: themeColors.red, backdropFilter: 'blur(16px)', width: '100%', mt: 2 }}>
                       <Typography variant="h5" sx={{ color: themeColors.darkBackground, fontFamily: "ApfelGrotezkRegular" }}>No pubs found matching your filters</Typography>
                       <Typography variant="body1" sx={{ mt: 2, color: 'rgba(34, 34, 34, 0.7)', fontFamily: "ApfelGrotezkRegular" }}>Try adjusting your filters or search in a different location</Typography>
                   </Paper>
                )}
                {/* Pubs List or Map View */}
                {!propLoading && hasInitiallyFetched.current && processedPubs.length > 0 && !coverageError && (
                    tab === 'list' ? (
                        <Box className="pubsList" sx={{ alignItems: 'center', minHeight: '50vh', width: '100%', maxWidth: 800 }}>
                           {processedPubs.map((pub, index) => (
                               <React.Fragment key={pub.google_place_id}>
                                   <Box sx={{ mb: 4, width: '100%' }} data-pub-id={pub.google_place_id}>
                                       <PubCard 
                                            pub={pub} 
                                            timelineMode={timelineMode} 
                                            raycastLoadingForThisPub={raycastingPubIds.has(pub.google_place_id)} 
                                            onViewPub={handleViewPub} 
                                            onSubmitGarden={handleSubmitGarden}
                                            isBot={isBot}
                                        />
                                   </Box>
                                   {/* Show in-feed ad after every 3rd pub (indices 2, 5, 8, etc.) */}
                                   {(index + 1) % 3 === 0 && !isBot && (
                                       <Box sx={{ mb: 4, width: '100%' }}>
                                           <AdSense 
                                               client="ca-pub-3335995580070945" 
                                               slot="1820372142" 
                                               format="fluid" 
                                               layoutKey="-7g+eh+16-4o+6v"
                                               width="100%" 
                                           />
                                       </Box>
                                   )}
                               </React.Fragment>
                           ))}
                       </Box>
                    ) : (
                        <MapView latitude={latitude} longitude={longitude} pubsToView={processedPubs} />
                    )
                )}
            </Box>
            {tab === 'list' && (<ScrollIndicator iconColor={themeColors.orange} threshold={100} size={32} bottom={10} right={10} hideOnlyAtBottom={true} forceVisible={true} zIndex={2000} />)}
            <Box sx={{ px: '5%' }}><Footer /></Box>
        </Box>
    );
};
export default FindPubs;